import React from 'react';
import VAdmin from "#root/pages/VAdmin";
import PageNotFound from "#root/pages/Errors/404";
import BadRequest from "#root/pages/Errors/400";
import InternalServerError from "#root/pages/Errors/500";
import ServiceUnavailable from "#root/pages/Errors/503";
import ServerDown from "#root/pages/Errors/ServerDown";
import Unauthorized from "#root/pages/Errors/401";
import StatusReview from '#root/pages/VAdmin/StatusReview';
import MatchVictims from "#root/pages/VAdmin/MatchVictims";
import FamilyAssistance from "#root/pages/VAdmin/FamilyAssistance";
import AddFamilyAssistance from "#root/pages/VAdmin/FamilyAssistance/AddFamilyAssistance";
import EditFamilyAssistance from "#root/pages/VAdmin/FamilyAssistance/EditFamilyAssistance";
import FamilyAssistanceEntries from "#root/pages/VAdmin/FamilyAssistance/FormEntries";
import VictimReunification from "#root/pages/VAdmin/VictimReunification";
import VTracker from '#root/pages/VTracker';
import Home from '#root/pages/Home';
import AllVictims from '#root/pages/VTracker/AllVictims';
import MetaData from '#root/layouts/MetaData';
import PublicRequestForm from '#root/pages/PublicRequestForm';
import { ROUTES } from './constants';
import UsersAdministration from '#root/pages/OrganizationSettings/UsersAdministration';
import DefaultUnits from '#root/pages/OrganizationSettings/DefaultUnits';
import UserSettings from '#root/pages/OrganizationSettings/UserSettings';
import OrganizationSettings from '#root/pages/OrganizationSettings';
import Locations from '#root/pages/OrganizationSettings/Locations';
import { ROLES } from '#root/utils/roles';
import AddUser from '#root/pages/OrganizationSettings/UsersAdministration/AddUser';
import MapVictims from '#root/pages/VAdmin/MapVictims';
import AllContacts from '#root/pages/VTracker/AllContacts';
import AddContact from '#root/pages/VTracker/AllContacts/AddContact';
import EditContact from '#root/pages/VTracker/AllContacts/EditContact';
import ViewerMap from '#root/pages/ViewerMap';
import AddVictim from '#root/pages/VTracker/AllVictims/AddVictim';
import EditVictim from '#root/pages/VTracker/AllVictims/EditVictim';
import AddOrganization from '#root/pages/Home/AddOrganization';
import EditOrganization from '#root/pages/Home/EditOrganization';
import UserAccountRequests from '#root/pages/OrganizationSettings/UserAccountRequests';
import ChiefComplaints from '#root/pages/OrganizationSettings/ChiefComplaints';
import AfterActionReport from '#root/pages/VAdmin/AfterActionReport';
import HotWashPage from '#root/pages/VTracker/HotWash';
import SuperAdminUserManagement from '#root/pages/EmcMerrgeSettings/SuperAdminUserManagement';
import CreateSuperAdmin from '#root/pages/EmcMerrgeSettings/SuperAdminUserManagement/CreateSuperAdmin';
import EditSuperAdmin from '#root/pages/EmcMerrgeSettings/SuperAdminUserManagement/EditSuperAdmin';
import EmcMerrgeSettings from '#root/pages/EmcMerrgeSettings';
import AddResponderRequest from '#root/pages/Merrge211/AddResponderRegistration';
import RolesPage from '#root/pages/OrganizationSettings/Roles';
import ResponderCheckIn from '#root/pages/Merrge211/ResponderCheckIn';
import Merrge211 from '#root/pages/Merrge211';
import ApprovedResponders from '#root/pages/Merrge211/ApprovedResponders';
import CheckInResponder from '#root/pages/Merrge211/ApprovedResponders/CheckInResponder';
import CheckedInDeployable from '#root/pages/Merrge211/CheckedInDeployable';
import MealTracking from '#root/pages/Merrge211/MealTracking';
import ChangeStatus from '#root/pages/Merrge211/CheckedInDeployable/ChangeStatus';
import StatusReport from '#root/pages/Merrge211/StatusReport';
import TimeAndTravel from '#root/pages/Merrge211/TimeAndTravel';
import TimeAndTravelDetails from '#root/pages/Merrge211/TimeAndTravel/TimeAndTravelDetails';
import IncidentLocations from '#root/pages/Merrge211/IncidentLocations';
import ResponderTeamForm from '#root/pages/Merrge211/ResponderTeamForm';
import StatusMap from '#root/pages/Merrge211/StatusMap';

export interface RouteComponentProps {
    userRole?: string;
}

export interface RouteConfig {
    path: string;
    element: React.ComponentType<RouteComponentProps>;
    title: string;
    description?: string;
    requireAuth?: boolean;
    roles?: string[];
    children?: RouteConfig[];
}

const withTitle = (Component: React.ComponentType<RouteComponentProps>, title?: string, description?: string) => {
    return function TitledComponent(props: RouteComponentProps) {
        return (
            <>
                <MetaData title={title} description={description} />
                <Component {...props} />
            </>
        );
    };
};

// Process routes to add metadata based on hierarchy
const processRoutes = (routes: RouteConfig[], parent?: RouteConfig): RouteConfig[] => {
    return routes.map(route => {
        const fullTitle = parent ? `${route.title} | ${parent.title}` : route.title;

        return {
            ...route,
            roles: route.roles || parent?.roles,
            element: withTitle(route.element, fullTitle, route.description),
            children: route.children ?
                processRoutes(route.children, route) :
                undefined
        };
    });
};

const rawRoutes: RouteConfig[] = [
    {
        path: ROUTES.PUBLIC_REQUEST,
        element: PublicRequestForm,
        title: "Public Request Form",
        requireAuth: false
    },
    {
        path: ROUTES.FAMILY_ASSISTANCE_ENTRY,
        element: AddFamilyAssistance,
        title: "Family Assistance Entry",
        requireAuth: false
    },
    {
        path: ROUTES.RESPONDER_REGISTRATION,
        element: AddResponderRequest,
        title: "Responder Registration Form",
        requireAuth: false
    },
    {
        path: ROUTES.ROOT,
        element: Home,
        title: "Home",
        requireAuth: true,
        children: [
            {
                path: ROUTES.CREATE_ORGANIZATION,
                element: AddOrganization,
                title: "Create New Organization",
                roles: [ROLES.GLOBALADMIN],
            },
            {
                path: ROUTES.EDIT_ORGANIZATION,
                element: EditOrganization,
                title: "Edit Organization",
                roles: [ROLES.GLOBALADMIN],
            },
            {
                path: ROUTES.EMC_MERRGE_SETTINGS.ROOT,
                element: EmcMerrgeSettings,
                title: "EMC MERRGE Settings",
                children: [
                    {
                        path: ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_USER_MANAGEMENT,
                        element: SuperAdminUserManagement,
                        title: "SuperAdmin User Management",
                        roles: [ROLES.GLOBALADMIN],
                        children: 
                        [
                            {
                                path: ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_CREATE,
                                element: CreateSuperAdmin,
                                title: "Create New SuperAdmin",
                            },
                            {
                                path: ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_EDIT,
                                element: EditSuperAdmin,
                                title: "Edit SuperAdmin",
                            },
                        ]
                    },
                ]
            },
            {
                path: ROUTES.ORGANIZATION_SETTINGS.ROOT,
                element: OrganizationSettings,
                title: "Organization Settings",
                roles: [ROLES.ADMIN, ROLES.GLOBALADMIN],
                children: [
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.USERS_ADMINISTRATION,
                        element: UsersAdministration,
                        title: "Users Administration",
                        children: [
                            {
                                path: ROUTES.ORGANIZATION_SETTINGS.ADD_USER,
                                element: AddUser,
                                title: "Add User",
                            }
                        ]
                    },
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.LOCATIONS,
                        element: Locations,
                        title: "Locations",
                    },
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.DEFAULT_UNITS,
                        element: DefaultUnits,
                        title: "Default Units",
                    },
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.ACCOUNT_REQUESTS,
                        element: UserAccountRequests,
                        title: "User Account Requests",
                    },
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.CHIEF_COMPLAINTS,
                        element: ChiefComplaints,
                        title: "Chief Complaints",
                    },
                    {
                        path: ROUTES.ORGANIZATION_SETTINGS.ROLES,
                        element: RolesPage,
                        title: "Roles",
                    },

                ]
            },
            {
                path: ROUTES.USER_SETTINGS,
                element: UserSettings,
                title: "User Settings"
            },
            {
                path: ROUTES.VIEWER_MAP,
                element: ViewerMap,
                title: "Viewer Map",
                roles: [ROLES.MANAGER, ROLES.ADMIN, ROLES.GLOBALADMIN, ROLES.VIEWER],
            }
        ]
    },
    {
        path: ROUTES.VADMIN.ROOT,
        element: VAdmin,
        title: "VAdmin",
        roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.GLOBALADMIN],
        children: [
            {
                path: ROUTES.VADMIN.GENERAL_CONTACTS,
                element: AllContacts,
                title: "General Contacts",
                children: [
                    {
                        path: ROUTES.VADMIN.EDIT_CONTACTS,
                        element: EditContact,
                        title: "Edit Contact",
                    }
                ]
            },
            {
                path: ROUTES.VADMIN.MATCH_VICTIMS,
                element: MatchVictims,
                title: "Match Victims"
            },
            {
                path: ROUTES.VADMIN.VICTIM_REUNIFICATION,
                element: VictimReunification,
                title: "Victim Reunification"
            },
            {
                path: ROUTES.VADMIN.STATUS_REVIEW,
                element: StatusReview,
                title: "Status Review"
            },
            {
                path: ROUTES.VADMIN.MAP_VICTIMS,
                element: MapVictims,
                title: "Map Victims"
            },
            {
                path: ROUTES.VADMIN.FAMILY_ASSISTANCE,
                element: FamilyAssistance,
                title: "Family Assistance",
                children: [
                    {
                        path: ROUTES.VADMIN.FAMILY_ASSISTANCE_ENTRIES,
                        element: FamilyAssistanceEntries,
                        title: "Form Entries",
                        children: [
                            {
                                path: ROUTES.VADMIN.EDIT_FAMILY_ASSISTANCE,
                                element: EditFamilyAssistance,
                                title: "Edit Form Entry"
                            }
                        ]
                    }
                ]
            },
            {

                path: ROUTES.VADMIN.AFTER_ACTION_REPORT,
                element: AfterActionReport,
                title: "After Action Report"
            },
        ]
    },
    {
        path: ROUTES.VTRACKER.ROOT,
        element: VTracker,
        title: "VTracker",
        roles: [ROLES.USER, ROLES.ADMIN, ROLES.MANAGER, ROLES.GLOBALADMIN],
        children: [
            {
                path: ROUTES.VTRACKER.ALL_VICTIMS,
                element: AllVictims,
                title: "All Victims",
                children: [
                    {
                        path: ROUTES.VTRACKER.ADD_VICTIM,
                        element: AddVictim,
                        title: "Add Victim",
                    },
                    {
                        path: ROUTES.VTRACKER.EDIT_VICTIM,
                        element: EditVictim,
                        title: "Edit Victim",
                    }
                ]
            },
            {
                path: ROUTES.VTRACKER.ALL_CONTACTS,
                element: AllContacts,
                title: "All Contacts",
                children: [
                    {
                        path: ROUTES.VTRACKER.ADD_CONTACT,
                        element: AddContact,
                        title: "Add General Contact",
                    },
                    {
                        path: ROUTES.VTRACKER.EDIT_CONTACT,
                        element: EditContact,
                        title: "Edit General Contact",
                    }
                ]
            },
            {
                path: ROUTES.VTRACKER.HOT_WASH,
                element: HotWashPage,
                title: "Hot Wash"
            }
        ]
    },
    {
        path: ROUTES.MERRGE211.ROOT,
        element: Merrge211,
        title: "MERRGE™ 211",
        roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.GLOBALADMIN],
        children: [
            {
                path: ROUTES.MERRGE211.RESPONDER_CHECK_IN,
                element: ResponderCheckIn,
                title: "Responder Check In"
            },
            {
                path: ROUTES.MERRGE211.APPROVED_RESPONDERS,
                element: ApprovedResponders,
                title: "Approved Responders", 
                children: [
                    {
                        path: ROUTES.MERRGE211.CHECK_IN_RESPONDER,
                        element: CheckInResponder,
                        title: "Review Responder Details"
                    },
                    {
                        path: ROUTES.MERRGE211.EDIT_RESPONDER_TEAM_FORM,
                        element: ResponderTeamForm,
                        title: "Review Team’s Details"
                    }
                ]
            },
            {
                path: ROUTES.MERRGE211.CHECKED_IN_DEPLOYABLE,
                element: CheckedInDeployable,
                title: "Checked In Responders",
                children: [
                    {
                        path: ROUTES.MERRGE211.CHANGE_STATUS,
                        element: ChangeStatus,
                        title: "Change Responder Status"
                    }
                ]
            },
            {
                path: ROUTES.MERRGE211.MEAL_TRACKING,
                element: MealTracking,
                title: "Meal Tracking"
            },
            {

                path: ROUTES.MERRGE211.STATUS_REPORT,
                element: StatusReport,
                title: "Status Report"
            },
            {
                path: ROUTES.MERRGE211.TIME_AND_TRAVEL,
                element: TimeAndTravel,
                title: "Time and Travel",
                children: [
                    {
                        path: ROUTES.MERRGE211.TIME_AND_TRAVEL_DETAILS,
                        element: TimeAndTravelDetails,
                        title: "Time and Travel Details"
                    }
                ]
            },
            {
                path: ROUTES.MERRGE211.STATUS_MAP,
                element: StatusMap,
                title: "Status Map"
            },
            {
                path: ROUTES.MERRGE211.INCIDENT_LOCATIONS,
                element: IncidentLocations,
                title: "Incident Locations"
            }
        ]
    }
];

const rawErrorRoutes: RouteConfig[] = [
    {
        path: ROUTES.ERROR_404,
        element: PageNotFound,
        title: "Page Not Found",
        requireAuth: false
    },
    {
        path: ROUTES.ERROR_400,
        element: BadRequest,
        title: "Bad Request",
        requireAuth: false
    },
    {
        path: ROUTES.ERROR_500,
        element: InternalServerError,
        title: "Internal Server Error",
        requireAuth: false
    },
    {
        path: ROUTES.ERROR_503,
        element: ServiceUnavailable,
        title: "Service Unavailable",
        requireAuth: false
    },
    {
        path: ROUTES.ERROR_401,
        element: Unauthorized,
        title: "Unauthorized",
        requireAuth: false
    },
    {
        path: ROUTES.SERVER_DOWN,
        element: ServerDown,
        title: "Server Down",
        requireAuth: false
    },
];

export const routes = processRoutes(rawRoutes);
export const errorRoutes = processRoutes(rawErrorRoutes);
